/**
 * REK — Basisrekenen rulebook.
 *
 * Sourced from DEFAULT_POLICY (Basisrekenen entries) +
 * docs/audits/DIDACTISCH_AUDIT_REK_2026.md +
 * MODULE_AUTHORING_GUIDE §2.
 */

import type { ModuleRulebook } from './types'

export const REK_RULEBOOK: ModuleRulebook = {
  moduleId: 'Basisrekenen',
  shortCode: 'REK',
  displayName: 'Basisrekenen',

  didactics: {
    principles: [
      'strategie_eerst',
      'cijferend_vs_handig',
      'tafels_recall',
      'context_herkenning',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt', 'Reken Zeker'],
    misconceptionDomain: 'rek',
    coreSummary:
      'Beheersing van basisbewerkingen + bewust kiezen van strategie (cijferend vs. handig) bij contextopgaven.',
  },

  notation: {
    requireCommaDecimals: true,
    forbiddenPatterns: [
      {
        pattern: '\\d+\\.\\d+',
        label: 'Punt als decimaalteken (gebruik komma: 0,5)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'tafels|cijferend|optellen|aftrekken|vermenigvuldigen|delen|handig|schatten|volgorde|splitsen|compenseren|rijgen|lenen|onthouden',
  },

  explanation: {
    requireRuleReference: true,
    // Vanaf tier 9 (groep 7) wordt strategiekeuze bewust gemaakt — leerlingen
    // moeten dan kunnen verantwoorden WAAROM ze handig of cijferend kiezen.
    checkStrategyMinTier: 9,
    preferredStrategyKeywords: [
      'splitsen',
      'compenseren',
      'rijgen',
      'verdubbelen',
      'halveren',
      'aanvullen',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'column',
      'numberline',
      'numberBoard',
      'contextSpotlight',
      'barModel',
      'strategySteps',
    ],
    familyVisualHints: [
      // — Cijferend rekenen (kolomopzet primair) —
      {
        familyIdPattern: '^REK-CIJF-',
        preferredStimulus: 'column',
        note: 'Cijferend rekenen vraagt om een kolom-visual met tussenresultaten.',
      },

      // — Handig rekenen: meerdere visual-keuzes per sub-familie —
      // Voor PLUS-MIN past de strategie-stappen-kaart het sterkst (splitsen,
      // compenseren als expliciete stappen-keten). Numberline blijft prima
      // alternatief voor sprongen-georiënteerde families.
      {
        familyIdPattern: '^REK-HANDIG-PLUS-MIN',
        preferredSupport: 'strategySteps',
        note: 'Handig rekenen plus/min: strategySteps-kaart toont splitsen of compenseren als concrete stappen-keten. Numberline mag als alternatief voor sprongen-vormen.',
      },
      {
        familyIdPattern: '^REK-HANDIG-',
        preferredSupport: 'numberline',
        note: 'Handig rekenen via getallenlijn met strategie-sprongen (splitsen, compenseren, aanvullen). Alternatief: strategySteps-kaart voor families waar de stappen-keten didactisch sterker is.',
      },

      // — Contextopgaven (strookmodel) —
      {
        familyIdPattern: '^REK-CONTEXT-',
        preferredSupport: 'barModel',
        note: 'Singapore-strookmodel maakt verdeel-/verhoudings-relaties in contextsommen zichtbaar.',
      },

      // — Tafels (numberBoard / geen visual; recall-georiënteerd) —
      {
        familyIdPattern: '^REK-TAFELS-BASIS',
        preferredSupport: 'numberBoard',
        note: 'Tafels-basis is recall — geen verplichte stimulus, numberBoard als optionele oefen-support.',
      },
      {
        familyIdPattern: '^REK-TAFELS-(UITBREIDEN|TOEPASSEN)',
        preferredSupport: 'numberline',
        note: 'Tafels uitbreiden/toepassen: numberline visualiseert sprongen van X.',
      },

      // — Schatten (getallenlijn met ankerpunten) —
      {
        familyIdPattern: '^REK-SCHATTEN',
        preferredStimulus: 'numberline',
        note: 'Schatten: getallenlijn met buurankers maakt "tussen welke ronde getallen?" zichtbaar.',
      },

      // — Volgorde der bewerkingen (regel-kaart) —
      {
        familyIdPattern: '^REK-VOLGORDE',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Voorrangsregels (× en ÷ vóór + en −, haakjes eerst): regel-kaart als geheugensteun.',
      },

      // — Gemengde toetsen (geen vaste visual; afhankelijk van bewerking) —
      {
        familyIdPattern: '^REK-GEMENGD-',
        autoExemptEmbedded: true,
        note: 'Gemengde sets: visual volgt per vraag de bewerking (kolom voor cijferend, numberline voor handig).',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'cijferend_rekenen',
      'cijferend_delen',
      'step_solver',
      'multiplication_grid',
      'bar_model_part',
    ],
    discouragedTypes: [
      {
        type: 'text_highlight',
        reason: 'Niet geschikt voor numerieke bewerkingen.',
      },
    ],
    customTypeRoles: [
      {
        type: 'bar_model_part',
        didacticRole: 'strategic',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Strookmodel bij contextopgaven — kind leest een deel af en herkent zo de bewerking voordat hij rekent.',
      },
      {
        type: 'cijferend_rekenen',
        didacticRole: 'fluency',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Kolomopzet voor +/−/×: tussenresultaten + lenen/onthouden expliciet — fluency-georiënteerd.',
      },
      {
        type: 'cijferend_delen',
        didacticRole: 'fluency',
        preferredDok: 2,
        appropriateTiers: { min: 7, max: 14 },
        reason:
          'Staartdeling stap-voor-stap: visueel zichtbare deelstappen — fluency vanaf groep 6.',
      },
      {
        type: 'multiplication_grid',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 10 },
        reason:
          'Tafelrooster verbindt rij x kolom met product — concept-versteviging voor recall.',
      },
      {
        type: 'step_solver',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Meerstaps-redenering bij contextopgaven; kind moet bewerking + volgorde verantwoorden.',
      },
      {
        type: 'strategy_solver',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Strategiekeuze expliciet maken (splitsen vs. compenseren vs. cijferend) — kern van handig rekenen op DOK 3.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^REK-TAFELS-',
        preferredTypes: ['fill_in', 'multiple_choice', 'multiplication_grid'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'Tafels zijn recall: exact getal invullen of op rooster aanwijzen — geen oordeel of schatting.',
      },
      {
        familyIdPattern: '^REK-CIJF-(PLUS|MIN)',
        preferredTypes: ['cijferend_rekenen', 'fill_in'],
        discouragedTypes: ['multiple_choice', 'true_false_series'],
        note: 'Cijferend +/− vereist kolomopzet met tussenresultaten — MC verbergt de procedure.',
      },
      {
        familyIdPattern: '^REK-CIJF-KEER',
        preferredTypes: ['cijferend_rekenen', 'fill_in', 'multiplication_grid'],
        discouragedTypes: ['multiple_choice'],
        note: 'Cijferend × vraagt expliciete tussenproducten — kolom of rooster, niet MC-keuze.',
      },
      {
        familyIdPattern: '^REK-CIJF-DEEL',
        preferredTypes: ['cijferend_delen', 'fill_in'],
        discouragedTypes: ['multiple_choice', 'true_false_series'],
        note: 'Staartdeling: stap-voor-stap visualisatie — MC-vorm trivialiseert het deelproces.',
      },
      {
        familyIdPattern: '^REK-HANDIG-',
        preferredTypes: ['strategy_solver', 'step_solver', 'fill_in'],
        discouragedTypes: ['true_false_series'],
        note: 'Handig rekenen draait om strategiekeuze — strategy_solver maakt splitsen/compenseren expliciet.',
      },
      {
        familyIdPattern: '^REK-CONTEXT-',
        preferredTypes: [
          'step_solver',
          'bar_model_part',
          'fill_in',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Contextopgaven: bewerking herkennen + uitrekenen — strookmodel of stap-voor-stap, geen oordeel.',
      },
      {
        familyIdPattern: '^REK-SCHATTEN',
        preferredTypes: ['estimation_slider', 'multiple_choice', 'fill_in'],
        discouragedTypes: ['true_false_series'],
        note: 'Schatten: slider of ronde-getal-keuze — exacte berekening is hier juist niet de bedoeling.',
      },
      {
        familyIdPattern: '^REK-(VOLGORDE|GEMENGD)',
        preferredTypes: ['fill_in', 'multiple_choice', 'step_solver'],
        discouragedTypes: ['estimation_slider'],
        note: 'Volgorde/gemengd: exact resultaat na bewerkings-volgorde — geen schatting.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'borrow-skip',
      label: 'Lenen overslaan',
      description:
        'Bij aftrekken vergeten te lenen — komt voor bij cijferend aftrekken.',
    },
    {
      id: 'table-neighbor',
      label: 'Buur-tafel',
      description:
        'Antwoord van de naburige tafel kiezen (7×8 vs 7×9 = 56 vs 63).',
    },
    {
      id: 'operation-swap',
      label: 'Bewerking verwisselen',
      description:
        'In contextopgave + ipv − of × ipv ÷ kiezen — meten of het kind de operatie herkent.',
    },
  ],

  keyRules: [
    'Strategienaam ALTIJD benoemen in feedback (splitsen, compenseren, rijgen)',
    'Cijferend rekenen: tussenresultaten correct, lenen bij aftrekken',
    'Contextopgaven: bewerking herkennen is de kern, niet het uitrekenen — overweeg barModel als support',
    'Tafels-basis: puur recall, geen contextverhalen',
    'Trap-distractors: buur-tafel, lenen overslaan, bewerking verwisselen',
  ],

  authoringGuideAnchor: '#2-rek--basisrekenen',
}
